import React, { useState, useEffect, useCallback } from 'react';
import archiveCategoryApi, { ArchiveCategory } from '../../api/archiveCategoryApi';
import { useToast } from '../../context/ToastContext';
import ArchiveCategoryHeader from '../../components/archive/category/ArchiveCategoryHeader';
import ArchiveCategorySearch from '../../components/archive/category/ArchiveCategorySearch';
import ArchiveCategoryTable from '../../components/archive/category/ArchiveCategoryTable';
import ArchiveCategoryModal from '../../components/archive/category/ArchiveCategoryModal';
import DeleteCategoryDialog from '../../components/archive/category/DeleteCategoryDialog';

const initialCategoryData: ArchiveCategory = {
  category_code: '',
  category_name: '',
  archive_type: 'general',
  applicable_modules: [],
  is_mandatory: false,
  validity_unit: 'permanent',
  requires_validation: true,
  is_active: true,
};

const typeColors = {
  debtor: 'bg-amber-100 text-amber-700 border border-amber-200',
  credit: 'bg-blue-100 text-blue-700 border border-blue-200',
  hris: 'bg-green-100 text-green-700 border border-green-200',
  legal: 'bg-purple-100 text-purple-700 border border-purple-200',
  general: 'bg-gray-50 text-gray-800 border border-gray-200',
};

export default function ArchiveCategoryList() {
  const { showSuccess, showError } = useToast();
  const [categories, setCategories] = useState<ArchiveCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modal, setModal] = useState<{ open: boolean; data: ArchiveCategory | null }>({
    open: false,
    data: null,
  });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    category: ArchiveCategory | null;
  }>({
    open: false,
    category: null,
  });

  const loadData = useCallback(async () => {
    try {
      const data = await archiveCategoryApi.getAll();
      setCategories(data);
    } catch (error: any) {
      showError('Gagal memuat data kategori: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openModal = (item: ArchiveCategory | null = null) => {
    setModal({
      open: true,
      data: item || { ...initialCategoryData },
    });
  };

  const closeModal = () => {
    setModal({ open: false, data: null });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal.data) return;

    setSaving(true);
    try {
      if (modal.data.id) {
        await archiveCategoryApi.update(modal.data.id, modal.data);
        showSuccess('Kategori arsip berhasil diperbarui');
      } else {
        await archiveCategoryApi.create(modal.data);
        showSuccess('Kategori arsip berhasil ditambahkan');
      }
      await loadData();
      closeModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Gagal menyimpan kategori';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (category: ArchiveCategory) => {
    setDeleteDialog({ open: true, category });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.category?.id) return;

    try {
      await archiveCategoryApi.delete(deleteDialog.category.id);
      showSuccess('Kategori arsip berhasil dihapus');
      await loadData();
      setDeleteDialog({ open: false, category: null });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Gagal menghapus kategori';
      showError(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, category: null });
  };

  const handleDataChange = (data: ArchiveCategory) => {
    setModal(prev => ({ ...prev, data }));
  };

  const filteredCategories = categories.filter(
    c =>
      c.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.category_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ArchiveCategoryHeader onAddClick={() => openModal(null)} />

      <ArchiveCategorySearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      <ArchiveCategoryTable
        categories={filteredCategories}
        typeColors={typeColors}
        onEdit={openModal}
        onDelete={handleDeleteClick}
      />

      <ArchiveCategoryModal
        open={modal.open}
        data={modal.data}
        saving={saving}
        onClose={closeModal}
        onSave={handleSave}
        onDataChange={handleDataChange}
      />

      <DeleteCategoryDialog
        open={deleteDialog.open}
        categoryName={deleteDialog.category?.category_name}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
